import { MainLayoutHeaderTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Page } from '@/design-system-v2/page';
import { cn } from '@/lib/utils';

import { settingsItemDomId } from './settings-search-dom';

export const Root = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <YStack className={cn('gap-6 w-full', className)}>{children}</YStack>;
};

export const Title = ({
  children,
  trailing,
}: {
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) => (
  <MainLayoutHeaderTunnel.In>
    <XStack
      data-testid="settings-section-title"
      className="min-w-0 items-center gap-2"
    >
      <Page.Title>{children}</Page.Title>
      {trailing}
    </XStack>
  </MainLayoutHeaderTunnel.In>
);

export const Description = ({ children }: { children: React.ReactNode }) => {
  return <p className="text-sm text-foreground-muted">{children}</p>;
};

export const Header = ({ children }: { children: React.ReactNode }) => {
  return <YStack className="gap-1 w-full empty:hidden">{children}</YStack>;
};

export const ItemLabel = ({
  children,
  id,
}: React.LabelHTMLAttributes<HTMLLabelElement>) => {
  return (
    <label htmlFor={id} className="text-foreground text-sm">
      {children}
    </label>
  );
};

export const ItemDescription = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <p className="text-xs text-foreground-muted">{children}</p>;
};

type GroupProps = {
  title?: string;
  trailing?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>;

export const Group = ({ children, title, trailing, ...props }: GroupProps) => {
  return (
    <YStack className="w-full gap-3" {...props}>
      {title && (
        <XStack className="items-baseline justify-between gap-4">
          <GroupHeader>{title}</GroupHeader>
          {trailing}
        </XStack>
      )}
      <YStack className="w-full border border-border bg-surface rounded-xl px-6 py-5 gap-5">
        {children}
      </YStack>
    </YStack>
  );
};

export const GroupHeader = ({ children }: { children: React.ReactNode }) => {
  return <p className="text-foreground-muted text-sm">{children}</p>;
};

export const ItemHeader = ({ children }: { children: React.ReactNode }) => {
  return <YStack className="gap-1">{children}</YStack>;
};

export const Seperator = () => {
  return (
    <div className="w-full h-px bg-border dark:bg-border-strong opacity-70" />
  );
};

type ItemProps = {
  direction?: 'horizontal' | 'vertical';
  /** Entry id from `settings-search.config.ts`, rendered as the row's DOM id */
  searchId?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const Item = ({
  children,
  direction = 'horizontal',
  searchId,
  id: providedId,
  ...props
}: ItemProps) => {
  const id = searchId ? settingsItemDomId(searchId) : providedId;
  if (direction === 'horizontal') {
    return (
      <XStack
        id={id}
        className="w-full gap-4 min-h-8 justify-between items-center"
        {...props}
      >
        {children}
      </XStack>
    );
  }

  return (
    <YStack
      id={id}
      className="w-full gap-2 justify-start items-start"
      {...props}
    >
      {children}
    </YStack>
  );
};
